import React, { useState } from 'react';
import { X, Crown, Zap, Gift, Check, Loader2, Tag } from 'lucide-react';
import { getStoredToken } from '../services/auth';

interface PurchasePageProps {
  isDarkMode: boolean;
  onClose: () => void;
  onPromoRedeemed: () => void;
}

export function PurchasePage({ isDarkMode, onClose, onPromoRedeemed }: PurchasePageProps) {
  const [promoCode, setPromoCode] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoMessage, setPromoMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleRedeemPromo = async () => {
    if (!promoCode.trim()) {
      setPromoMessage({ type: 'error', text: 'Please enter a promo code' });
      return;
    }

    setPromoLoading(true);
    setPromoMessage(null);

    try {
      const token = getStoredToken();
      if (!token) {
        throw new Error('You must be signed in to redeem promo codes');
      }

      const response = await fetch('/api/admin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'redeem-promo',
          code: promoCode,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to redeem promo code');
      }

      // Build success message based on what was granted
      let successText = 'Success!';
      const rewards: string[] = [];
      
      if (data.bonusGames > 0) {
        rewards.push(`${data.bonusGames} bonus games`);
      }
      if (data.bonusPredictions > 0) {
        rewards.push(`${data.bonusPredictions} bonus predictions`);
      }
      if (data.subscriptionType === 'pro') {
        rewards.push('Pro subscription');
      } else if (data.subscriptionType === 'max') {
        rewards.push('Max subscription');
      }
      
      if (rewards.length > 0) {
        successText += ` You received ${rewards.join(', ')}!`;
      }

      setPromoMessage({ 
        type: 'success', 
        text: successText
      });
      setPromoCode('');
      
      // Notify parent to refresh usage data
      onPromoRedeemed();
    } catch (error) {
      setPromoMessage({ 
        type: 'error', 
        text: error instanceof Error ? error.message : 'Failed to redeem promo code' 
      });
    } finally {
      setPromoLoading(false);
    }
  };

  const tiers = [
    {
      name: 'Free',
      price: '$0',
      period: 'forever',
      icon: Gift,
      iconColor: isDarkMode ? 'text-green-400' : 'text-green-600',
      bgGradient: isDarkMode 
        ? 'from-green-900/20 to-emerald-900/20' 
        : 'from-green-50 to-emerald-50',
      borderColor: isDarkMode ? 'border-green-500/30' : 'border-green-300',
      features: [
        '2 games per day',
        '100 predictions per day',
        'All game modes',
        'Daily challenges',
        'Leaderboard access',
      ],
      comingSoon: false,
    },
    {
      name: 'Pro',
      price: '$0.50',
      period: 'per month',
      icon: Crown,
      iconColor: isDarkMode ? 'text-blue-400' : 'text-blue-600',
      bgGradient: isDarkMode 
        ? 'from-blue-900/20 to-purple-900/20' 
        : 'from-blue-50 to-purple-50',
      borderColor: isDarkMode ? 'border-blue-500/30' : 'border-blue-300',
      features: [
        'Unlimited games',
        '1600 predictions per day',
        'All game modes',
        'Daily challenges',
        'Leaderboard access',
        'Pro badge',
      ],
      comingSoon: true,
    },
    {
      name: 'Max',
      price: '$2',
      period: 'per month',
      icon: Zap,
      iconColor: isDarkMode ? 'text-purple-400' : 'text-purple-600',
      bgGradient: isDarkMode 
        ? 'from-purple-900/20 to-pink-900/20' 
        : 'from-purple-50 to-pink-50',
      borderColor: isDarkMode ? 'border-purple-500/30' : 'border-purple-300',
      features: [
        'Unlimited games',
        'Unlimited predictions',
        'All game modes',
        'Daily challenges',
        'Leaderboard access',
        'Max badge',
        'Priority support',
      ],
      comingSoon: true,
    },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className={`rounded-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto ${
        isDarkMode ? 'bg-gray-900' : 'bg-white'
      }`}>
        {/* Header */}
        <div className={`sticky top-0 z-10 px-6 py-4 border-b flex items-center justify-between ${
          isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Choose Your Plan
          </h2>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg transition-colors ${
              isDarkMode 
                ? 'hover:bg-gray-800 text-gray-400 hover:text-gray-200' 
                : 'hover:bg-gray-100 text-gray-600 hover:text-gray-900'
            }`}
          >
            <X size={24} />
          </button>
        </div>

        {/* Pricing Tiers */}
        <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
          {tiers.map((tier, index) => {
            const Icon = tier.icon;
            return (
              <div
                key={tier.name}
                className={`rounded-xl border-2 p-6 flex flex-col bg-gradient-to-br ${tier.bgGradient} ${tier.borderColor}`}
              >
                {/* Icon & Name */}
                <div className="flex items-center gap-3 mb-4">
                  <div className={`p-3 rounded-full ${
                    isDarkMode ? 'bg-gray-800' : 'bg-white'
                  }`}>
                    <Icon className={`${tier.iconColor}`} size={32} />
                  </div>
                  <div>
                    <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {tier.name}
                    </h3>
                    {index === 1 && (
                      <span className={`text-xs font-medium ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                        Best Value
                      </span>
                    )}
                  </div>
                </div>

                {/* Price */}
                <div className="mb-6">
                  <div className="flex items-baseline gap-1">
                    <span className={`text-4xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {tier.price}
                    </span>
                    <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {tier.period}
                    </span>
                  </div>
                </div>

                {/* Features */}
                <div className="flex-1 mb-6">
                  <ul className="space-y-3">
                    {tier.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-2">
                        <Check className={`flex-shrink-0 mt-0.5 ${tier.iconColor}`} size={18} />
                        <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* CTA Button */}
                {tier.comingSoon ? (
                  <div className={`py-3 px-4 rounded-lg text-center font-medium ${
                    isDarkMode 
                      ? 'bg-gray-800 text-gray-400' 
                      : 'bg-gray-100 text-gray-500'
                  }`}>
                    Coming Soon
                  </div>
                ) : (
                  <div className={`py-3 px-4 rounded-lg text-center font-medium ${
                    isDarkMode 
                      ? 'bg-green-900/50 text-green-400' 
                      : 'bg-green-100 text-green-700'
                  }`}>
                    Current Plan
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Promo Code Section */}
        <div className={`mx-6 mb-6 p-6 rounded-xl border-2 ${
          isDarkMode 
            ? 'bg-gradient-to-br from-orange-900/20 to-yellow-900/20 border-orange-500/30' 
            : 'bg-gradient-to-br from-orange-50 to-yellow-50 border-orange-300'
        }`}>
          <div className="flex items-center gap-2 mb-4">
            <Tag className={isDarkMode ? 'text-orange-400' : 'text-orange-600'} size={24} />
            <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Have a Promo Code?
            </h3>
          </div>
          
          <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Enter your promo code below to receive bonus games and predictions!
          </p>

          <div className="flex gap-3">
            <input
              type="text"
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === 'Enter' && handleRedeemPromo()}
              placeholder="ENTER CODE"
              className={`flex-1 px-4 py-3 rounded-lg font-mono text-lg uppercase focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                isDarkMode 
                  ? 'bg-gray-800 text-white border-gray-700' 
                  : 'bg-white text-gray-900 border-gray-300'
              } border`}
              disabled={promoLoading}
            />
            <button
              onClick={handleRedeemPromo}
              disabled={promoLoading || !promoCode.trim()}
              className={`px-6 py-3 rounded-lg font-bold transition-all flex items-center gap-2 ${
                promoLoading || !promoCode.trim()
                  ? (isDarkMode ? 'bg-gray-700 text-gray-500' : 'bg-gray-200 text-gray-400')
                  : (isDarkMode 
                    ? 'bg-orange-600 hover:bg-orange-700 text-white' 
                    : 'bg-orange-500 hover:bg-orange-600 text-white')
              } disabled:cursor-not-allowed`}
            >
              {promoLoading ? (
                <>
                  <Loader2 size={20} className="animate-spin" />
                  Redeeming...
                </>
              ) : (
                'Redeem'
              )}
            </button>
          </div>

          {/* Promo Message */}
          {promoMessage && (
            <div className={`mt-4 p-3 rounded-lg ${
              promoMessage.type === 'success'
                ? (isDarkMode ? 'bg-green-900/50 text-green-400' : 'bg-green-100 text-green-700')
                : (isDarkMode ? 'bg-red-900/50 text-red-400' : 'bg-red-100 text-red-700')
            }`}>
              {promoMessage.text}
            </div>
          )}
        </div>

        {/* Footer Note */}
        <div className={`px-6 pb-6 text-center text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          <p>Payment processing for Pro and Max subscriptions coming soon!</p>
          <p className="mt-1">Stay tuned for exclusive features and benefits.</p>
        </div>
      </div>
    </div>
  );
}
